import { useState } from 'react';
import { queryClient, queryKeys } from './query-client';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { filePath, isMemoryPath, type BrowsableFile } from './file-browser';

// The client half of "Delete" — forgetting a MEMORY: POST the explorer path, then make the account-wide
// list re-read so the row is gone from the browser and the Files tiles alike.
//
// The rule about WHAT may be deleted is not here — it is `planMemoryDelete` in common-lib, which the route
// and the native bridge both run, so a client cannot talk any surface into deleting something that is not
// memory by asking nicely. `isMemoryPath` is checked here too, but as a courtesy: it saves a round trip
// that would end in a 403, and it is the same question the row's menu asked before offering the action.
// This module only asks, reports, and refreshes.

export type FileDeleteState = 'idle' | 'deleting' | 'error';

export function useFileDelete() {
  const [state, setState] = useState<FileDeleteState>('idle');

  /** Forgets `item`. Resolves to true on success. */
  const remove = async (item: BrowsableFile): Promise<boolean> => {
    const path = filePath(item);
    if (!isMemoryPath(path)) {
      setState('error');
      return false;
    }
    setState('deleting');
    try {
      const res = await apiFetch(`${API_BASE}/api/delete-file`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ path }),
      });
      if (!res.ok) {
        setState('error');
        return false;
      }
      // Memory belongs to the account, not to a Space, so only the account-wide list carries it — the
      // cross-workspace `/files` browser and the Files summary tiles read that one entry.
      await queryClient.invalidateQueries({ queryKey: queryKeys.allWorkspaceFiles });
      setState('idle');
      return true;
    } catch {
      setState('error');
      return false;
    }
  };

  return { state, remove, reset: () => setState('idle') };
}
